export default {
  en: {
    alreadyExists: 'The environment {env} already exists for this project',
    add: 'Add environment',
    delete: 'Delete environment',
    removeFromProject: 'Remove from project',
    settings: 'Environment settings',
    description: 'An environment is an isolated workspace where components are deployed. It often represent a stage in your development lifecycle, such as develop, staging, or production.',
    find: 'Find an environment',
    info: 'Environment info',
    nameInvalid: 'Only spaces, hyphens, underscores and alphanumeric characters allowed. Minimum length is 2 characters.',
    new: 'New environment',
    type: 'Environment type',
    useCase: 'Use case',
    component: {
      add: 'Add component',
      alreadyExists: 'The component "{component}" already exists in this environment',
      create: 'Create component',
      description: 'Components are instances of a stack, configured using variables you define.',
      delete: 'Delete component',
      deleteConfirm: 'Do you still want to delete this component?',
      deleteInfo: 'Deleting a component is a permanent action that cannot be undone. Resources created through pipelines will not be deleted automatically. Please remove them via the pipeline job or manually as they may generate costs.',
      deleteInfoPipelineHook: 'Deleting a component is permanent and cannot be undone. This stack defines a deletion hook for components: confirming delete starts that pipeline action so infrastructure tied to this component can be torn down as your platform team configured. You may get a link to the pipeline build in the success notification.',
      skipPipelineHook: 'Delete immediately in Cycloid (skip pipeline deletion hook)',
      skipPipelineHookHint: 'Use only to recover from a stuck state. Your stack may expect a pipeline hook to destroy cloud resources first.',
      deleteConfigFilesBehaviorLabel: 'Deletion option',
      deleteConfigFilesBehaviorStandard: 'Standard deletion',
      deleteConfigFilesBehaviorIgnoreConfig: 'Delete ignoring config repository errors',
      deleteConfigFilesBehaviorHint: 'Only choose “ignore config repository errors” if deletion fails because Git or config files cannot be updated (for example an unreachable config repo). The default is recommended.',
      find: 'Find a component',
      learnMore: 'Learn more about components',
      nameInvalid: 'Only spaces, hyphens, underscores and alphanumeric characters allowed. Minimum length is 2 characters.',
      settings: 'Component settings',
      info: 'Component info',
      name: 'Component name',
      empty: 'No components in this environment yet',
    },
  },
  es: {
    alreadyExists: 'El entorno {env} ya existe en este proyecto',
    add: 'Agrega un entorno',
    delete: 'Eliminar entorno',
    removeFromProject: 'Quitar del proyecto',
    settings: 'Configuración del entorno',
    description: 'Un entorno es un espacio de trabajo aislado donde se implementan componentes. A menudo representa una etapa en su ciclo de vida de desarrollo, como desarrollo, pruebas o producción.',
    find: 'Encuentra un entorno',
    info: 'Información del entorno',
    nameInvalid: 'Solo se permiten espacios, guiones, guiones bajos y caracteres alfanuméricos. La longitud mínima es de 2 caracteres.',
    new: 'Nuevo entorno',
    type: 'Tipo de entorno',
    useCase: 'Caso de uso',
    component: {
      add: 'Añadir componente',
      alreadyExists: 'El componente "{component}" ya existe en este entorno',
      create: 'Crear componente',
      description: 'Los componentes son instancias de un stack, configurados utilizando variables que definas.',
      delete: 'Eliminar componente',
      deleteConfirm: '¿Todavía quieres eliminar este componente?',
      deleteInfo: 'Eliminar un componente es una acción permanente que no se puede deshacer. Los recursos creados a través de pipelines no se eliminarán automáticamente. Por favor, elimínelos a través del trabajo de la pipeline o manualmente, ya que pueden generar costos.',
      deleteInfoPipelineHook: 'Eliminar un componente es permanente y no se puede deshacer. Esta stack define un hook de eliminación para los componentes: al confirmar se iniciará esa acción en el pipeline para que la infraestructura asociada a este componente pueda destruirse según lo configurado por tu equipo de plataforma. Es posible que veas un enlace al build del pipeline en la notificación de éxito.',
      skipPipelineHook: 'Eliminar ya en Cycloid (omitir hook de eliminación del pipeline)',
      skipPipelineHookHint: 'Solo para desbloqueos. El stack puede requerir antes un hook que destruya recursos en la nube.',
      deleteConfigFilesBehaviorLabel: 'Opción de eliminación',
      deleteConfigFilesBehaviorStandard: 'Eliminación estándar',
      deleteConfigFilesBehaviorIgnoreConfig: 'Eliminar ignorando errores del repositorio de configuración',
      deleteConfigFilesBehaviorHint: 'Usa “ignorar errores del repositorio de configuración” solo si la eliminación falla porque Git o los archivos de configuración no se pueden actualizar (por ejemplo el repo de configuración no está disponible). Por defecto se recomienda la opción estándar.',
      find: 'Encuentra un componente',
      learnMore: 'Obtén más información sobre los componentes',
      nameInvalid: 'Solo se permiten espacios, guiones, guiones bajos y caracteres alfanuméricos. La longitud mínima es de 2 caracteres.',
      settings: 'Configuración del componente',
      info: 'Información del componente',
      name: 'Nombre del componente',
      empty: 'No hay componentes en este entorno todavía',
    },
  },
  fr: {
    alreadyExists: `L'environnement {env} existe déjà pour ce projet`,
    add: 'Ajouter un environnement',
    delete: `Supprimer l'environnement`,
    removeFromProject: 'Retirer du projet',
    settings: `Paramètres de l'environnement`,
    description: `Un environnement est un espace de travail isolé où les composants sont déployés. Il représente souvent une étape dans votre cycle de vie de développement, comme le développement, la mise en scène ou la production.`,
    find: 'Rechercher un environnement',
    info: `Infos de l'environnement`,
    nameInvalid: `Seuls les espaces, les caractères alphanumériques, traits d'union et tirets bas sont permis. La longueur minimale est de 2 caractères.`,
    new: 'Nouvel environnement',
    type: `Type d'environnement`,
    useCase: `Cas d'usage`,
    component: {
      add: `Ajouter un composant`,
      alreadyExists: `Le composant "{component}" existe déjà dans cet environnement`,
      create: `Créer un composant`,
      description: `Les composants sont des instances d'une stack, configurés à l'aide de variables que vous définissez.`,
      delete: 'Supprimer le composant',
      deleteConfirm: 'Voulez-vous toujours supprimer ce composant ?',
      deleteInfo: `La suppression d'un composant est une action permanente qui ne peut pas être annulée. Les ressources créées via les pipelines ne seront pas supprimées automatiquement. Veuillez les supprimer via le job de la pipeline ou manuellement, car elles peuvent générer des coûts.`,
      deleteInfoPipelineHook: `La suppression d'un composant est définitive et ne peut pas être annulée. Cette stack définit un hook de suppression pour les composants : en confirmant, cette action pipeline est lancée afin que l'infrastructure liée à ce composant puisse être démantelée comme configuré par votre équipe plateforme. Un lien vers le build pipeline peut apparaître dans la notification de succès.`,
      skipPipelineHook: 'Supprimer tout de suite dans Cycloid (ignorer le hook pipeline de suppression)',
      skipPipelineHookHint: 'Réservé au déblocage. Le stack peut attendre un hook qui détruit d’abord l’infra.',
      deleteConfigFilesBehaviorLabel: 'Option de suppression',
      deleteConfigFilesBehaviorStandard: 'Suppression standard',
      deleteConfigFilesBehaviorIgnoreConfig: 'Supprimer en ignorant les erreurs du dépôt de configuration',
      deleteConfigFilesBehaviorHint: `Choisissez « ignorer les erreurs du dépôt de configuration » uniquement si la suppression échoue parce que Git ou les fichiers de configuration ne peuvent pas être mis à jour (par exemple dépôt inaccessible). L’option standard est recommandée.`,
      find: 'Rechercher un composant',
      learnMore: 'En savoir plus sur les composants',
      nameInvalid: `Seuls les espaces, les caractères alphanumériques, traits d'union et tirets bas sont permis. La longueur minimale est de 2 caractères.`,
      settings: 'Paramètres du composant',
      info: `Infos du composant`,
      name: `Nom du composant`,
      empty: 'Pas encore de composants dans cet environnement',
    },
  },
}
